import React, { useEffect } from "react";
import { useLocation, NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import clsx from "clsx";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SidenavCollapse from "common/Sidenav/SidenavCollapse";
import NestedSidenavCollapse from "./NestedSidenavCollapse";
import styles from "common/Sidenav/styles/sidenav";
import ConciliaDirectoLogo from "assets/images/logoCD.svg";
import { useSoftUIController } from "context";
import { useAuth } from "auth-context/auth.context";

function Sidenav({ routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const classes = styles({ miniSidenav, transparentSidenav });
  const location = useLocation();
  const { pathname } = location;

  const { user, permissions } = useAuth();
  const isUserAdmin = user?.roles?.includes("ADMIN");

  const closeSidenav = () => dispatch({ type: "MINI_SIDENAV", value: true });

  useEffect(() => {
    function handleMiniSidenav() {
      dispatch({
        type: "MINI_SIDENAV",
        value: window.innerWidth < 1200,
      });
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Función auxiliar para verificar permisos
  const checkPermission = (route) => {
    if (!route.access) return true;

    // Verificar si la ruta tiene permiso explícito
    const hasExplicitPermission = permissions?.some(
      (perm) =>
        perm.route === (route.type === "title" ? `title-${route.key}` : route.route) &&
        perm.has_access
    );

    switch (route.access) {
      case "protected":
        // Para rutas protegidas necesita permiso explícito (adminAccess: ADMIN también accede)
        return hasExplicitPermission || (route.adminAccess === true && isUserAdmin);
      case "ADMIN":
        // Para rutas ADMIN, basta con ser admin
        return isUserAdmin;
      case "all_auth":
        return !!user?.token;
      case "public":
        return true;
      default:
        return true;
    }
  };

  // Filtrar rutas basadas en los permisos del usuario
  const filteredRoutes = routes.filter((route, index) => {
    if (route.type === "title") {
      // Primero verificar si el título tiene acceso según su tipo
      if (!checkPermission(route)) {
        return false;
      }

      // Luego verificar si hay elementos visibles después del título
      const nextRoutes = routes.slice(index + 1);
      const nextVisible = nextRoutes.find((next) =>
        next.type === "title"
          ? checkPermission(next)
          : next.type === "nested"
          ? next.children.some((child) => checkPermission(child))
          : checkPermission(next)
      );
      return !!nextVisible;
    }

    if (route.type === "nested") {
      // Para rutas anidadas, verificar si al menos un hijo es accesible
      const hasAccessibleChild = route.children.some((child) => checkPermission(child));
      return hasAccessibleChild;
    }

    return checkPermission(route);
  });

  const renderRoutes = filteredRoutes.map(
    ({ type, name, icon, title, noCollapse, key, route, href, children }) => {
      let returnValue;

      if (type === "collapse") {
        const isActive = pathname === route;

        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            className={classes.sidenav_navlink}
          >
            <SidenavCollapse name={name} icon={icon} active={isActive} noCollapse={noCollapse} />
          </Link>
        ) : (
          <NavLink to={route} key={key} className={classes.sidenav_navlink}>
            <SidenavCollapse name={name} icon={icon} active={isActive} noCollapse={noCollapse} />
          </NavLink>
        );
      } else if (type === "title") {
        returnValue = (
          <SuiTypography
            key={key}
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            customClass={classes.sidenav_title}
          >
            {title}
          </SuiTypography>
        );
      } else if (type === "divider") {
        returnValue = <Divider key={key} />;
      } else if (type === "nested") {
        const isActive = children.some((child) => pathname === child.route);
        returnValue = (
          <NestedSidenavCollapse key={key} name={name} icon={icon} active={isActive}>
            {children
              .filter((child) => checkPermission(child))
              .map((child) => (
                <NavLink
                  to={child.route}
                  key={`${key}-${child.key}`}
                  className={classes.sidenav_navlink}
                  onClick={(e) => e.stopPropagation()}
                >
                  <SidenavCollapse
                    name={child.name}
                    icon={child.icon}
                    active={pathname === child.route}
                    noCollapse={true}
                  />
                </NavLink>
              ))}
          </NestedSidenavCollapse>
        );
      }

      return returnValue;
    }
  );

  return (
    <Drawer
      {...rest}
      variant="permanent"
      classes={{
        paper: clsx(classes.sidenav, {
          [classes.sidenav_open]: !miniSidenav,
          [classes.sidenav_close]: miniSidenav,
        }),
      }}
    >
      <SuiBox customClass={classes.sidenav_header}>
        <SuiBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          customClass="cursor-pointer"
          onClick={closeSidenav}
        >
          <SuiTypography variant="h6" textColor="secondary">
            <Icon className="font-bold">close</Icon>
          </SuiTypography>
        </SuiBox>
        <NavLink to="/">
          <SuiBox
            component="img"
            src={ConciliaDirectoLogo}
            alt="Concilia Directo Logo"
            customClass={classes.sidenav_logo}
          />
          <SuiBox customClass={classes.sidenav_logoLabel}></SuiBox>
        </NavLink>
      </SuiBox>
      <Divider />
      <List>{renderRoutes}</List>
      <SuiBox customClass={classes.sidenav_footer}>
        <SuiBox mt={2}></SuiBox>
      </SuiBox>
    </Drawer>
  );
}

Sidenav.propTypes = {
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
